export const PWA_UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;
export const PWA_SAFE_APPLY_POLL_MS = 60 * 1000;
export const PWA_UPDATE_HARD_CAP_MS = 4 * 60 * 60 * 1000;

const PROTECTED_PATH_PREFIXES = [
  "/book",
  "/packages",
  "/my-cart",
  "/my-bookings",
  "/account",
];

export function isUpdateProtectedPath(pathname: string): boolean {
  return PROTECTED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

interface PwaUpdateCoordinatorOptions {
  applyUpdate: () => Promise<unknown>;
  getRegistration: () => ServiceWorkerRegistration | undefined;
  getPathname?: () => string;
  now?: () => number;
}

/**
 * Checks for new deployments and activates a waiting worker at the next safe moment.
 * Booking, checkout, account-edit and reschedule screens are deferred so an update cannot
 * discard in-progress input. The hard cap bounds stale pricing/API behavior for abandoned tabs.
 */
export function startPwaUpdateCoordinator({
  applyUpdate,
  getRegistration,
  getPathname = () => window.location.pathname,
  now = Date.now,
}: PwaUpdateCoordinatorOptions) {
  let pendingSince: number | null = null;
  let applying = false;

  const checkForUpdate = () => {
    void getRegistration()?.update().catch(() => {
      // The browser retries service-worker checks; a transient offline failure is non-fatal.
    });
  };

  const applyWhenSafe = () => {
    if (pendingSince === null || applying) return;
    const hardCapReached = now() - pendingSince >= PWA_UPDATE_HARD_CAP_MS;
    if (isUpdateProtectedPath(getPathname()) && !hardCapReached) return;

    applying = true;
    void applyUpdate().catch(() => {
      applying = false;
    });
  };

  const onVisibilityChange = () => {
    if (document.visibilityState !== "visible") return;
    checkForUpdate();
    applyWhenSafe();
  };

  const updateTimer = window.setInterval(checkForUpdate, PWA_UPDATE_CHECK_INTERVAL_MS);
  const safeApplyTimer = window.setInterval(applyWhenSafe, PWA_SAFE_APPLY_POLL_MS);
  window.addEventListener("focus", applyWhenSafe);
  window.addEventListener("popstate", applyWhenSafe);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return {
    notifyUpdateAvailable() {
      pendingSince ??= now();
      applyWhenSafe();
    },
    checkForUpdate,
    applyWhenSafe,
    stop() {
      window.clearInterval(updateTimer);
      window.clearInterval(safeApplyTimer);
      window.removeEventListener("focus", applyWhenSafe);
      window.removeEventListener("popstate", applyWhenSafe);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    },
  };
}
